import { TechnicalConfigDrawer } from "features/shared/components/technical-config-tab/TechnicalConfigDrawer";
import { formatTechnicalConfigDrawerSubtitle } from "features/shared/lib/technical-config-tab/drawerFormatters";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { WindowTypeGroup } from "../model/types";

import {
  useDeleteWindowConfig,
  useGetWindowTypeCatalog,
  useGetWindowTypeDetail,
  useSaveWindowConfig,
} from "../api/hooks";
import { useWindowConfigForm } from "../hooks/useWindowConfigForm";

type WindowConfigDrawerProps = {
  group: null | WindowTypeGroup;
  onClose: () => void;
  open: boolean;
  projectId: string;
};

export const WindowConfigDrawer = ({ group, onClose, open, projectId }: WindowConfigDrawerProps) => {
  const { t } = useTranslation();
  const userDefinedType = group?.userDefinedType;
  const { data: detail, isLoading } = useGetWindowTypeDetail(projectId, userDefinedType);
  const { data: catalog = [], isLoading: isCatalogLoading } = useGetWindowTypeCatalog(open);
  const { isLoading: isSaving, mutate: saveConfig } = useSaveWindowConfig();
  const { isLoading: isDeleting, mutate: deleteConfig } = useDeleteWindowConfig();

  const { fields, mapPayload, tabs } = useWindowConfigForm({
    catalog,
    enabled: open,
    hasTechnology: group?.hasTechnology ?? false,
    initialTechnology: detail?.technology,
    isCatalogLoading,
    isLoading,
    recordKey: userDefinedType,
  });

  const handleSave = () => {
    if (!userDefinedType || !group) return;
    const payload = mapPayload();
    if (!payload) {
      toast.error(t("TechnicalData.windows.nonEmptyTypeAndFrame"));
      return;
    }
    saveConfig(
      { data: payload, hasTechnology: group.hasTechnology, projectId, userDefinedType },
      { onSuccess: onClose }
    );
  };

  const handleDelete = () => {
    if (!userDefinedType || !group?.hasTechnology) return;
    deleteConfig({ projectId, userDefinedType }, { onSuccess: onClose });
  };

  return (
    <TechnicalConfigDrawer
      deleteLabel={String(t("TechnicalData.windows.removeConfig"))}
      fields={fields}
      headerColumns={2}
      isConfigured={Boolean(group?.hasTechnology)}
      isDeleting={isDeleting}
      isLoading={isLoading || isCatalogLoading}
      isSubmitting={isSaving}
      onClose={onClose}
      onDelete={handleDelete}
      onSave={handleSave}
      open={open}
      subtitle={
        group
          ? formatTechnicalConfigDrawerSubtitle(group.surfaceCount, t("TechnicalData.windows.windows"), group.totalArea)
          : undefined
      }
      tabs={tabs}
      title={group?.userDefinedType ?? t("TechnicalData.windows.drawerTitle")}
    />
  );
};
